import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { TICKET_TYPE_LABEL, type TicketType } from '@veta-desk/domain';
import { Shell } from '../components/Shell';
import { api, ApiError, type Meta, type TriageItem } from '../lib/api';
import { dateTime, relativeTime } from '../lib/format';

// Correos que el lector no pudo asignar solos: remitente de un cliente sin dominio registrado,
// remitente ajeno respondiendo a un ticket, o respuesta a un ticket cerrado.

const REASON: Record<TriageItem['reason'], { label: string; help: string }> = {
  unknown_org: { label: 'Remitente sin cliente', help: 'El dominio del remitente no pertenece a ningún cliente. Elige el cliente para crear el ticket (y registra su dominio en Configuración → Correo para la próxima).' },
  sender_mismatch: { label: 'Remitente no reconocido', help: 'Respondió a un ticket existente, pero no es el solicitante ni alguien del dominio del cliente. Revisa antes de agregarlo.' },
  closed_ticket: { label: 'Respuesta a ticket cerrado', help: 'El ticket ya estaba cerrado. Si es un tema nuevo, crea un ticket relacionado.' }
};

const field = 'h-9 rounded-md border border-[#D6D3CD] bg-white px-2 text-sm';

function Item({ item, meta }: { item: TriageItem; meta: Meta | undefined }) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [org, setOrg] = useState('');
  const [type, setType] = useState<TicketType>('INC');
  const [error, setError] = useState('');
  const done = () => {
    void queryClient.invalidateQueries({ queryKey: ['triage'] });
    void queryClient.invalidateQueries({ queryKey: ['tickets'] });
  };
  const convert = useMutation({
    mutationFn: (body: Record<string, unknown>) => api<{ ticketNumber: string }>(`/mail/triage/${item.id}/convert`, { method: 'POST', body: JSON.stringify(body) }),
    onSuccess: r => { done(); navigate(`/tickets/${r.ticketNumber}`); },
    onError: e => setError(e instanceof ApiError ? e.message : 'No se pudo convertir')
  });
  const discard = useMutation({
    mutationFn: () => api(`/mail/triage/${item.id}/discard`, { method: 'POST' }),
    onSuccess: done,
    onError: e => setError(e instanceof ApiError ? e.message : 'No se pudo descartar')
  });
  const reason = REASON[item.reason];

  return (
    <li className="rounded-lg border border-line bg-white p-4">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <div className="min-w-0">
          <p className="text-sm"><strong className="font-semibold">{item.fromName || item.from}</strong> <span className="text-muted">&lt;{item.from}&gt;</span></p>
          <h2 className="mt-0.5 font-semibold">{item.subject || '(sin asunto)'}</h2>
        </div>
        <span className="text-xs text-muted" title={dateTime(item.receivedAt)}>{relativeTime(item.receivedAt)}</span>
      </div>
      <p className="mt-2 inline-flex items-center gap-2 rounded bg-[#FDF5EC] px-2 py-1 text-xs text-[#9A3412]">
        <strong className="font-semibold">{reason.label}</strong>
        {item.ticketNumber && <> · <Link to={`/tickets/${item.ticketNumber}`} className="underline">{item.ticketNumber}</Link></>}
      </p>
      <p className="mt-1 text-xs text-muted">{reason.help}</p>
      <p className="mt-3 max-h-40 overflow-auto whitespace-pre-line rounded-md bg-[#FAFAF8] p-3 text-sm">{item.text || '(sin texto)'}</p>
      {item.attachments.length > 0 && (
        <p className="mt-2 text-xs text-muted">Adjuntos: {item.attachments.map(a => a.fileName).join(', ')}</p>
      )}
      {error && <p role="alert" className="mt-2 text-sm text-[#8F1D1D]">{error}</p>}

      <div className="mt-4 flex flex-wrap items-center gap-2">
        {item.reason === 'sender_mismatch' && item.ticketNumber && (
          <button type="button" className="inline-flex h-9 items-center rounded-md bg-accent px-3 text-sm font-semibold text-white hover:bg-accent-dark disabled:opacity-50"
            disabled={convert.isPending} onClick={() => convert.mutate({})}>
            Agregar al ticket {item.ticketNumber}
          </button>
        )}
        <label className="sr-only" htmlFor={`org-${item.id}`}>Cliente</label>
        <select id={`org-${item.id}`} className={field} value={org} onChange={e => setOrg(e.target.value)}>
          <option value="">Elige el cliente…</option>
          {meta?.organizations.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
        </select>
        <label className="sr-only" htmlFor={`type-${item.id}`}>Tipo</label>
        <select id={`type-${item.id}`} className={field} value={type} onChange={e => setType(e.target.value as TicketType)}>
          {(['INC', 'SOL', 'REQ'] as TicketType[]).map(ty => <option key={ty} value={ty}>{TICKET_TYPE_LABEL[ty]}</option>)}
        </select>
        <button type="button" disabled={!org || convert.isPending}
          className={`inline-flex h-9 items-center rounded-md px-3 text-sm font-semibold disabled:opacity-50 ${item.reason === 'sender_mismatch' ? 'border border-[#D6D3CD] bg-white text-[#3F3D39] hover:bg-paper' : 'bg-accent text-white hover:bg-accent-dark'}`}
          onClick={() => convert.mutate({ organizationId: org, type })}>
          Crear ticket
        </button>
        <button type="button" disabled={discard.isPending} onClick={() => discard.mutate()}
          className="ml-auto inline-flex h-9 items-center rounded-md px-3 text-sm text-muted hover:bg-paper hover:text-ink">
          Descartar
        </button>
      </div>
    </li>
  );
}

export default function MailTriage() {
  const query = useQuery({ queryKey: ['triage'], queryFn: () => api<TriageItem[]>('/mail/triage'), refetchInterval: 60_000 });
  const meta = useQuery({ queryKey: ['meta'], queryFn: () => api<Meta>('/meta'), staleTime: 5 * 60_000 });
  return (
    <Shell>
      <div className="flex max-w-4xl flex-col gap-4">
        <div>
          <h1 className="text-2xl font-semibold">Correos por clasificar</h1>
          <p className="mt-1 text-sm text-muted">Correos que llegaron al buzón de soporte y no se pudieron asignar solos a un cliente o a un ticket.</p>
        </div>
        {query.isLoading && <p role="status" className="text-sm text-muted">Cargando…</p>}
        {query.data?.length === 0 && (
          <p className="rounded-lg border border-line bg-white p-8 text-center text-sm text-muted">Nada pendiente. Los correos de clientes conocidos se convierten en tickets solos.</p>
        )}
        <ul className="flex flex-col gap-3">
          {query.data?.map(item => <Item key={item.id} item={item} meta={meta.data} />)}
        </ul>
      </div>
    </Shell>
  );
}
